<template>
    <b-overlay
        variant="white"
        :show="loading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <b-card>
            <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <h4 class="mb-50 mb-md-0">Scheduler Task</h4>
                <div class="d-flex">
                    <b-button
                        variant="outline-secondary"
                        class="mr-1"
                        @click="$router.push({ name: 'system-scheduler-tasks' })"
                    >
                        Close
                    </b-button>
                    <b-button
                        v-if="canCancelTask"
                        variant="outline-danger"
                        class="mr-1"
                        :disabled="actionInProgress"
                        @click="cancelTask"
                    >
                        Cancel
                    </b-button>
                    <b-button
                        v-if="canRescheduleTask"
                        variant="primary"
                        :disabled="actionInProgress || !rescheduleDateTime"
                        @click="rescheduleTask"
                    >
                        Reschedule
                    </b-button>
                </div>
            </div>

            <b-form-group label="Reschedule datetime">
                <b-form-input
                    v-model="rescheduleDateTime"
                    type="datetime-local"
                    :disabled="actionInProgress"
                />
            </b-form-group>

            <b-row>
                <b-col md="6">
                    <p><strong>ID:</strong> {{ task.id || '-' }}</p>
                    <p><strong>Status:</strong> {{ task.status || '-' }}</p>
                    <p><strong>Task Type:</strong> {{ task.taskType || '-' }}</p>
                    <p><strong>Planned At:</strong> {{ formatDateTime(task.plannedAt) }}</p>
                    <p><strong>Next Execution:</strong> {{ formatDateTime(task.nextExecutionTime) }}</p>
                    <p><strong>Run At:</strong> {{ formatDateTime(task.runAt) }}</p>
                    <p><strong>Executed At:</strong> {{ formatDateTime(task.executedAt) }}</p>
                </b-col>
                <b-col md="6">
                    <p><strong>Executed Times:</strong> {{ task.executedTimes !== undefined && task.executedTimes !== null ? task.executedTimes : '-' }}</p>
                    <p><strong>Terminal Failures:</strong> {{ task.terminalFailures !== undefined && task.terminalFailures !== null ? task.terminalFailures : '-' }}</p>
                    <p><strong>Last Failed At:</strong> {{ formatDateTime(task.lastFailedAt) }}</p>
                    <p><strong>Cron Expression:</strong> {{ task.cronExpression || '-' }}</p>
                    <p><strong>Title:</strong> {{ task.metadata && task.metadata.title ? task.metadata.title : '-' }}</p>
                    <p><strong>Description:</strong> {{ task.metadata && task.metadata.description ? task.metadata.description : '-' }}</p>
                    <p><strong>Subscriber ID:</strong> {{ task.metadata && task.metadata.subscriberId ? task.metadata.subscriberId : '-' }}</p>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <p><strong>Last Error:</strong></p>
                    <pre class="p-1 bg-light">{{ task.lastError || '-' }}</pre>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <p><strong>Metadata Context:</strong></p>
                    <pre class="p-1 bg-light">{{ metadataContext }}</pre>
                </b-col>
            </b-row>
        </b-card>
    </b-overlay>
</template>

<script>
import { BCard, BOverlay, BButton, BFormGroup, BFormInput, BRow, BCol } from 'bootstrap-vue'
import { SystemApi } from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import dateRenderer from '@core/mixins/date-renderer'

export default {
    components: {
        BCard,
        BOverlay,
        BButton,
        BFormGroup,
        BFormInput,
        BRow,
        BCol,
    },
    mixins: [backendResponse, dateRenderer],
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            actionInProgress: false,
            task: {},
            rescheduleDateTime: '',
        }
    },
    computed: {
        canCancelTask() {
            return this.$can('view', 'button:system-scheduler-tasks-cancel')
        },
        canRescheduleTask() {
            return this.$can('view', 'button:system-scheduler-tasks-reschedule')
        },
        metadataContext() {
            return JSON.stringify((this.task.metadata && this.task.metadata.context) || {}, null, 2)
        },
    },
    created() {
        this.loadTask()
    },
    methods: {
        formatDateTime(value) {
            return value ? this.renderDateTime(value) : '-'
        },
        toDateTimeLocalValue(value) {
            if (!value) {
                return ''
            }

            return this.$moment(value).format('YYYY-MM-DDTHH:mm')
        },
        loadTask() {
            this.loading = true
            return SystemApi.scheduler.getTask(this.id)
                .then(r => {
                    this.task = r.data || {}
                    this.rescheduleDateTime = this.toDateTimeLocalValue(this.task.nextExecutionTime)
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.loading = false
                })
        },
        cancelTask() {
            if (!this.canCancelTask) {
                return
            }

            this.actionInProgress = true

            return SystemApi.scheduler.cancelTask(this.id)
                .then(() => {
                    this.showDefaultSuccessMessage('Task cancelled successfully')
                    this.$router.push({ name: 'system-scheduler-tasks' })
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.actionInProgress = false
                })
        },
        rescheduleTask() {
            if (!this.canRescheduleTask || !this.rescheduleDateTime) {
                return
            }

            this.actionInProgress = true

            return SystemApi.scheduler.rescheduleTask(this.id, {
                nextExecutionTime: this.$moment(this.rescheduleDateTime).toISOString(),
            })
                .then(() => {
                    this.showDefaultSuccessMessage('Task rescheduled successfully')
                    return this.loadTask()
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.actionInProgress = false
                })
        },
    },
}
</script>
